import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback
} from 'react';
import type { ReactNode } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { useUserStore } from '../stores/userStore';
import { SessionManager } from '../utils/sessionManager';
import { getEnvironmentId, clearEnvironmentId } from '../utils/environmentId';
import { OfflineSessionManager } from '../utils/offlineSessionManager';
import { getIsOnline } from '../services/connectivityService';

export const buildLoginUrl = (returnUrl: string): string =>
  `/bff/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;

type AuthContextType = {
  initialized: boolean;
  authenticated: boolean;
  offlineMode: boolean;
  offlineLocked: boolean;
  login: () => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Module-level CSRF token — shared with API clients via getCsrfToken()
let csrfToken: string | null = null;

export const getCsrfToken = (): string | null => csrfToken;

type Props = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: Props) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [offlineMode, setOfflineMode] = useState(false);
  const [offlineLocked, setOfflineLocked] = useState(false);

  useEffect(() => {
    const attemptOfflineMode = async () => {
      const marker = await OfflineSessionManager.getMarker();

      if (!marker) {
        setOfflineLocked(true);
        setInitialized(true);
        return;
      }

      useUserStore
        .getState()
        .setUser(
          marker.userName,
          marker.employeeId,
          marker.employeeCompany,
          marker.fullName,
          marker.profileName,
          marker.email,
          marker.locationName
        );

      await SessionManager.initializeSession(marker.userName);

      setAuthenticated(true);
      setOfflineMode(true);
      setInitialized(true);
    };

    const bootstrap = async () => {
      if (!getIsOnline()) {
        return await attemptOfflineMode();
      }

      try {
        const res = await fetch('/bff/auth/me', { credentials: 'include' });

        if (res.status === 401) {
          const hasValidMarker =
            await OfflineSessionManager.isOfflineSessionValid();
          if (hasValidMarker) {
            return await attemptOfflineMode();
          }
          window.location.href = buildLoginUrl(window.location.href);
          return;
        }

        if (!res.ok) {
          return await attemptOfflineMode();
        }

        const data = await res.json();

        csrfToken = data.csrfToken;

        localStorage.removeItem('auth-token');

        const userName = data.userName ?? '';
        const email = data.email;

        try {
          const environmentId = getEnvironmentId();
          const sessionResponse = await fetch('/bff/session/current', {
            credentials: 'include',
            headers: {
              'X-Environment-Id': environmentId ?? ''
            }
          });

          if (sessionResponse.ok) {
            const sessionData = await sessionResponse.json();

            useUserStore
              .getState()
              .setUser(
                sessionData.userName ?? userName,
                sessionData.employeeId ?? undefined,
                sessionData.employeeCompanyName ?? undefined,
                sessionData.fullName ?? undefined,
                sessionData.profileName ?? undefined,
                sessionData.email ?? email,
                sessionData.locationName ?? undefined
              );

            await OfflineSessionManager.saveMarker({
              userName: sessionData.userName ?? userName,
              email: sessionData.email ?? email ?? '',
              employeeId: sessionData.employeeId ?? '',
              employeeCompany: sessionData.employeeCompanyName ?? '',
              fullName: sessionData.fullName ?? '',
              locationName: sessionData.locationName ?? '',
              profileName: sessionData.profileName ?? '',
              lastServerValidation: Date.now(),
              lastActivity: Date.now()
            });
          } else {
            // Don't redirect to login here — the user is already authenticated
            // with the BFF (me endpoint succeeded). A failure here means the
            // upstream enterprise API is unavailable, not that auth is invalid.
            throw new Error(`Session fetch failed: ${sessionResponse.status}`);
          }
        } catch (error) {
          console.error('Failed to fetch session data:', error);

          // Fall back to cached offline session marker for user data
          const marker = await OfflineSessionManager.getMarker();
          if (marker?.employeeId) {
            useUserStore
              .getState()
              .setUser(
                marker.userName,
                marker.employeeId,
                marker.employeeCompany,
                marker.fullName,
                marker.profileName,
                marker.email,
                marker.locationName
              );
          } else {
            useUserStore
              .getState()
              .setUser(
                userName,
                undefined,
                undefined,
                undefined,
                undefined,
                email
              );
          }
        }

        await SessionManager.initializeSession(userName);

        setAuthenticated(true);
        setOfflineMode(false);
        setInitialized(true);
      } catch {
        return await attemptOfflineMode();
      }
    };

    bootstrap();
  }, []);

  const handleLogin = useCallback(() => {
    window.location.href = buildLoginUrl(window.location.href);
  }, []);

  const handleLogout = useCallback(async () => {
    await OfflineSessionManager.clearMarker();
    clearEnvironmentId();

    const session = SessionManager.getCurrentSession();
    await SessionManager.terminateSession(session?.userName);

    try {
      const response = await fetch('/bff/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': csrfToken ?? ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.redirectUrl;
      } else {
        window.location.href = buildLoginUrl(window.location.href);
      }
    } catch {
      window.location.href = buildLoginUrl(window.location.href);
    }
  }, []);

  if (!initialized) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        initialized,
        authenticated,
        offlineMode,
        offlineLocked,
        login: handleLogin,
        logout: handleLogout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
